import { Injectable } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { createSelector, select, Store } from "@ngrx/store";
import { UserState } from "@app/user/user.reducer";
import { take, map, takeUntil } from "rxjs/operators";
import { combineLatest, Subject } from "rxjs";
import { OrganizationDto } from "@helper/abstraction/organization";
import { Observable } from "rxjs";
import { notNull } from "@helper/operators";
import { getUser, getUserSuccess } from "@app/user/customization/customization-store/management.actions";
import { Actions, ofType } from "@ngrx/effects";
import { ManagementUserDto } from "@helper/abstraction/management";
import { ValidatorsUtil } from "@helper/validators-util";
import { UserProfile } from "@helper/abstraction/user";
import { Role } from "@helper/abstraction/roles";

@Injectable()
export class ManagementFormBuilderService {
	private eds: string | undefined;
	private unsubscribe$$: Subject<void> = new Subject<void>();
	constructor(
		private readonly fB: FormBuilder,
		private readonly store: Store<UserState>,
		private readonly actions$: Actions
	) {
		const user = (appState: any): UserState => appState.user;
		const userTypeState = createSelector(user, (state: UserState): UserProfile | undefined => state?.userProfile);
		this.store.pipe(select(userTypeState), takeUntil(this.unsubscribe$$)).subscribe(userProfile => {
			this.eds = userProfile?.port || "";
		});
	}

	public getFilterForm(): FormGroup {
		return this.fB.group({
			login: null,
			fullName: null,
			userStatuses: []
		});
	}

	public getUserForm(id?: number): Observable<FormGroup> {
		return id ? this.getFilledUserForm(id) : this.getEmptyUserForm();
	}

	public ngOnDestroy(): void {
		this.unsubscribe$$.next();
		this.unsubscribe$$.complete();
	}

	private getFilledUserForm(id: number): Observable<FormGroup> {
		this.store.dispatch(getUser(id));
		const selectUserState = (appState: any): UserState => appState.user;
		const selectOrganizationInfo = createSelector(selectUserState, (state: UserState): OrganizationDto | undefined => state.organizationInfo);
		const selectRoles = createSelector(selectUserState, (state: UserState): Role[] | undefined => state.roles);
		return combineLatest([
			this.store.pipe(select(selectOrganizationInfo), notNull()),
			this.store.pipe(select(selectRoles), notNull()),
			this.actions$.pipe(ofType(getUserSuccess)).pipe(map(action => action.user))
		]).pipe(map(([organizationInfo, roles, user]) => this.getForm(organizationInfo, roles, user)));
	}

	private getEmptyUserForm(): Observable<FormGroup> {
		const selectUserState = (appState: any): UserState => appState.user;
		const selectOrganizationInfo = createSelector(selectUserState, (state: UserState) => state.organizationInfo);
		const selectRoles = createSelector(selectUserState, (state: UserState): Role[] | undefined => state.roles);
		return combineLatest([
			this.store.pipe(select(selectOrganizationInfo), notNull()),
			this.store.pipe(select(selectRoles), notNull()),
		]).pipe(
			notNull(),
			take(1),
			map(([organizationInfo, roles]) => this.getForm(organizationInfo, roles))
		);
	}

	private getForm(organizationInfo: OrganizationDto | undefined, roles: Role[], user?: ManagementUserDto): FormGroup {
		const storageValue = user?.storageSet ? roles.some(el => el.id === "STORAGES2USER") ? [...user?.storageSet] : user?.storageSet[0] : null;

		const form = this.fB.group({
			id: [user && user.id],
			login: this.fB.control({ value: user && user.login || null, disabled: !!(user && user.login) }, { validators: [Validators.required, Validators.maxLength(255)] }),
			lastName: [user && user.lastName, [Validators.required, Validators.maxLength(255)]],
			firstName: [user && user.firstName, [Validators.required, Validators.maxLength(255)]],
			middleName: [user && user.middleName, Validators.maxLength(255)],
			jobPost: [user && user.jobPost, Validators.maxLength(255)],
			edsProxyPort: [user && user.port || this.eds, [Validators.required, ValidatorsUtil.checkEdsProxyPort()]],
			email: [user && user.email, [Validators.maxLength(135), Validators.required, ValidatorsUtil.emailCustomValidator()]],
			phoneNumber: [user && user.phoneNumber, Validators.maxLength(255)],
			userType: [user && user.userType ? user.userType === "USER" ? "TAX_USER" : "TAX_ADMIN" : "TAX_USER", [Validators.required]],
			organization: this.fB.control({ value: organizationInfo && { gln: organizationInfo.gln, legalName: organizationInfo.legalName, id: organizationInfo.id }, disabled: true }),
			storageSet: [storageValue],
			dateCreate: this.fB.control({ value: user && user.dateCreate ? new Date(user.dateCreate) : new Date(), disabled: true }),
			fullName: [user && user.fullName],
			iin: [user?.iin ? Number(user.iin) : null, [ValidatorsUtil.exactlyQuantityCharacters(12)]],
		});
		(form.get("email") as any).controlName = "Email";
		return form;
	}
}
